import { useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgEye, CgTrash } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";

const UsersList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [bulkDeleteModel, setBulkDeleteModel] = useState(false);

  useEffect(() => {
    const getUsers = async () => {
      setLoading(true);
      try {
        const response = await AuthInterceptor(
          `/admin/user?page=${pageNo}&limit=${limit}`
        );
        const data = response.data;

        if (data.s === 200) {
          setUsers(data.r || []);
          setTotalRecords(data.tr || 0);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch users:", err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    getUsers();
    setSelectedIds([]);
  }, [pageNo, limit]);

  const toggleUserStatus = async (id, newStatus) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/user/${id}`, {
        method: "PUT",
        body: JSON.stringify({ isActive: newStatus }),
      });
      const data = response.data;

      if (data.s === 200) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === id ? { ...user, isActive: newStatus } : user
          )
        );
        setSnack({
          notification: { message: "User status updated", type: "success" },
        });
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error updating user status", type: "error" },
      });
      console.error("Error updating user status:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = Array.isArray(users)
    ? users.filter(
        (u) =>
          u.fullName?.toLowerCase().includes(query.toLowerCase()) ||
          u.email?.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  const startIndex = (pageNo - 1) * limit;
  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const visibleIds = filteredUsers.map((user) => user._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length) {
      setSnack({
        notification: { message: "Select at least one user", type: "error" },
      });
      return;
    }
    // Deleting users also removes their orders, so ask first.
    if (action === "delete") {
      setBulkDeleteModel(true);
      return;
    }

    const isActive = action === "activate";
    // The status API fails when nothing changes, so skip users already in that state.
    const ids = selectedIds.filter((id) => users.find((u) => u._id === id)?.isActive !== isActive);

    try {
      setLoading(true);
      const results = await Promise.allSettled(
        ids.map((id) =>
          AuthInterceptor(`/admin/user/${id}`, {
            method: "PUT",
            body: JSON.stringify({ isActive }),
          })
        )
      );
      const updatedIds = ids.filter((id, i) => results[i].status === "fulfilled" && results[i].value?.data?.s === 200);

      setUsers((prevUsers) =>
        prevUsers.map((user) => (updatedIds.includes(user._id) ? { ...user, isActive } : user))
      );
      setSnack({
        notification:
          updatedIds.length === ids.length
            ? { message: "Bulk action completed", type: "success" }
            : { message: `Updated ${updatedIds.length} of ${ids.length} users`, type: "error" },
      });
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  const bulkDeleteUsers = async () => {
    const ids = [...selectedIds];
    try {
      setLoading(true);
      const results = await Promise.allSettled(
        ids.map((id) => AuthInterceptor(`/admin/user/${id}`, { method: "DELETE" }))
      );
      const deletedIds = ids.filter((id, i) => results[i].status === "fulfilled" && results[i].value?.data?.s === 200);

      setUsers((prevUsers) => prevUsers.filter((user) => !deletedIds.includes(user._id)));
      setSnack({
        notification:
          deletedIds.length === ids.length
            ? { message: "Users deleted successfully", type: "success" }
            : { message: `Deleted ${deletedIds.length} of ${ids.length} users`, type: "error" },
      });
      setSelectedIds([]);
    } finally {
      setLoading(false);
      setBulkDeleteModel(false);
    }
  };

  const viewUser = (user) => {
    navigate(`/user/${user._id}`);
  };

  const deleteUser = async (id) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/user/${id}`, {
        method: "DELETE",
      });
      const data = response.data;

      if (data.s === 200) {
        setUsers((prevUsers) => prevUsers.filter((user) => user._id !== id));
        setSnack({
          notification: { message: "User deleted successfully", type: "success" },
        });
      } else {
        throw new Error(data.m || "Error deleting user");
      }
    } catch (err) {
      setSnack({
        notification: { message: err.message || "Error deleting user", type: "error" },
      });
    } finally {
      setLoading(false);
      setDeleteModel(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <ListHeader
          title="Users"
          searchValue={query}
          searchPlaceholder="Search users..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => setPageNo(1) || setUsers([]) || setLimit(size)}
        />

        {/* Bulk actions (only when there are rows to select) */}
        {filteredUsers.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className="text-muted small">{selectedIds.length} selected</span>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("activate")}>
              Active
            </button>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("inactive")}>
              Inactive
            </button>
            <button className="btn btn-sm btn-outline-danger" onClick={() => runBulkAction("delete")}>
              Delete
            </button>
          </div>
        )}

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all users on this page"
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Name</th>
                <th>Email</th>
                <th >Mobile</th>
                <th >Role</th>
                <th>Orders</th>
                <th>Status</th>
                <th style={{ width: "150px" }} className="text-start">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                filteredUsers.length > 0 &&
                filteredUsers.map((u, idx) => (
                  <tr key={u._id || idx}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(u._id)}
                        onChange={() => toggleRowSelection(u._id)}
                        aria-label={`Select ${u.fullName || "user"}`}
                      />
                    </td>
                    <td>{startIndex + idx + 1}</td>
                    <td>{u.fullName || "-"}</td>
                    <td>{u.email || "-"}</td>
                    <td >
                      {u.mobileNumber || "-"}
                    </td>
                    <td >
                      <span className="badge text-primary">
                        {u.role || "-"}
                      </span>
                    </td>
                    <td>{u.totalOrders ?? 0}</td>
                    <td>
                      {u.isActive ? (
                        <span
                          className="badge bg-success"
                          role="button"
                          onClick={() => toggleUserStatus(u._id, false)}
                        >
                          Active
                        </span>
                      ) : (
                        <span
                          className="badge bg-secondary"
                          role="button"
                          onClick={() => toggleUserStatus(u._id, true)}
                        >
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => viewUser(u)}
                      >
                        <CgEye color="#666cff" />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger ms-2"
                        onClick={() => {
                          setDeleteId(u._id);
                          setDeleteModel(true);
                        }}
                      >
                        <CgTrash />
                      </button>
                    </td>
                  </tr>
                ))}

              {!loading && filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          show={totalRecords > 0}
          pageNo={pageNo}
          totalPages={totalPages}
          onPageChange={setPageNo}
        />
      </div>
      <DeleteConfirmModal
        show={deleteModel}
        count={1}
        singular="user"
        plural="users"
        loading={loading}
        onConfirm={() => deleteUser(deleteId)}
        onCancel={() => {
          setDeleteModel(false);
          setDeleteId(null);
        }}
      />
      <DeleteConfirmModal
        show={bulkDeleteModel}
        count={selectedIds.length}
        singular="user"
        plural="users"
        loading={loading}
        onConfirm={bulkDeleteUsers}
        onCancel={() => setBulkDeleteModel(false)}
      />
    </>
  );
};

export default UsersList;
